// LOT — Contrôle OFFBOARD réel : logique PURE partagée par ManualControl (cockpit physique unique).
import { isValidLatLng } from "@/lib/mapNavigation";
// Aucune I/O ici. La state machine WS/deadman/lease vit dans hooks/useRealOffboardControl.js.
//
// 4 axes BODY_NED (limites volontairement faibles pour le premier cockpit réel complet) :
//   vx > 0 = avant  | vx < 0 = arrière   (|vx| ≤ REAL_MAX_XY_M_S)
//   vy > 0 = droite | vy < 0 = gauche    (|vy| ≤ REAL_MAX_XY_M_S)
//   vz < 0 = montée | vz > 0 = descente  (|vz| ≤ REAL_MAX_Z_M_S)
//   yaw_rate > 0 = droite | < 0 = gauche (|yaw_rate| ≤ REAL_MAX_YAW_RATE_RAD_S)

export const REAL_MAX_XY_M_S = 0.25;        // m/s translation horizontale
export const REAL_MAX_Z_M_S = 0.20;         // m/s vitesse verticale
export const REAL_MAX_YAW_RATE_DEG_S = 10;  // deg/s vitesse de lacet
export const REAL_MAX_YAW_RATE_RAD_S = (REAL_MAX_YAW_RATE_DEG_S * Math.PI) / 180; // ≈ 0.174532925
export const REAL_STREAM_HZ = 10;    // fréquence du flux manual_velocity (alimente le deadman)
export const REAL_ACTIVE_HOLD_MS = 1000; // ~1 s de vitesse zéro après ACTIVE avant mouvement
export const REAL_DEFAULT_ALT = 2.0; // altitude initiale d'input (PAS une permission de décollage)
export const PRECHECK_REQUIRED = 8;  // préchecks entry-ready consécutifs (identique au chemin validé)

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export function zeroTarget() {
  return { vx: 0, vy: 0, vz: 0, yaw_rate: 0 };
}

// Entrées analogiques normalisées -1..+1 -> vitesses BODY_NED 4 axes clampées.
//   pitch -> vx | roll -> vy | lift -> vz (lift>0 = montée -> vz négatif) | yaw -> yaw_rate
export function mapAxes({ pitch = 0, roll = 0, lift = 0, yaw = 0 } = {}, limits = DEFAULT_LIMITS) {
  const L = _validLimits(limits);
  if (!L) return { vx: 0, vy: 0, vz: 0, yaw_rate: 0 };  // fail-closed : pas de limites Agent => zéro
  return {
    vx: clamp((pitch || 0) * L.max_xy, -L.max_xy, L.max_xy),
    vy: clamp((roll || 0) * L.max_xy, -L.max_xy, L.max_xy),
    vz: clamp(-(lift || 0) * L.max_z, -L.max_z, L.max_z),
    yaw_rate: clamp((yaw || 0) * L.max_yaw, -L.max_yaw, L.max_yaw),
  };
}

// Rétrocompat translation seule (pitch/roll). vz/yaw_rate à 0.
export function mapTranslation({ pitch = 0, roll = 0 } = {}) {
  const a = mapAxes({ pitch, roll });
  return { vx: a.vx, vy: a.vy, vz: 0, yaw_rate: 0 };
}

// Directions discrètes (joystick / clavier) -> contribution PAR AXE en m/s ou rad/s.
export const REAL_DIRECTIONS = {
  forward: { vx: REAL_MAX_XY_M_S },
  back: { vx: -REAL_MAX_XY_M_S },
  right: { vy: REAL_MAX_XY_M_S },
  left: { vy: -REAL_MAX_XY_M_S },
  up: { vz: -REAL_MAX_Z_M_S },     // montée
  down: { vz: REAL_MAX_Z_M_S },    // descente
  yaw_right: { yaw_rate: REAL_MAX_YAW_RATE_RAD_S },
  yaw_left: { yaw_rate: -REAL_MAX_YAW_RATE_RAD_S },
};

export function isRealDirection(dir) {
  return Object.prototype.hasOwnProperty.call(REAL_DIRECTIONS, dir);
}

// UNIFIED.3 — enveloppe de limites runtime. Défaut = anciennes constantes (fallback conservateur) ;
// l'AUTORITÉ réelle est fournie par l'Agent (control_limits) via le hook.
export const DEFAULT_LIMITS = { max_xy: REAL_MAX_XY_M_S, max_z: REAL_MAX_Z_M_S, max_yaw: REAL_MAX_YAW_RATE_RAD_S };

// Garde fail-closed : limites runtime valides (finies, > 0) sinon null. UNIFIED.3A : l'absence de
// limites Agent ne doit JAMAIS produire une consigne non nulle sur le chemin réel.
function _validLimits(L) {
  if (!L) return null;
  if (![L.max_xy, L.max_z, L.max_yaw].every((v) => Number.isFinite(v) && v > 0)) return null;
  return L;
}

// Profils de vitesse = RATIOS appliqués aux limites Agent (jamais des vitesses absolues).
export const SPEED_PROFILES = { slow: 0.25, normal: 0.50, rapid: 1.00 };
export const SPEED_ORDER = ["slow", "normal", "rapid"];
export const DEFAULT_SPEED_PROFILE = "slow";

export function nextSpeedProfile(p) {
  const i = SPEED_ORDER.indexOf(p);
  return i < 0 ? "normal" : SPEED_ORDER[Math.min(i + 1, SPEED_ORDER.length - 1)];
}
export function prevSpeedProfile(p) {
  const i = SPEED_ORDER.indexOf(p);
  return i < 0 ? "slow" : SPEED_ORDER[Math.max(i - 1, 0)];
}

// Valide les control_limits autoritaires de l'Agent. Renvoie {max_xy,max_z,max_yaw(rad),max_yaw_deg}
// ou null (fail-closed) si absent/non fini/<=0.
export function validateControlLimits(cl) {
  if (!cl) return null;
  const xy = Number(cl.max_xy_m_s), z = Number(cl.max_z_m_s), yawDeg = Number(cl.max_yaw_rate_deg_s);
  if (![xy, z, yawDeg].every((v) => Number.isFinite(v))) return null;
  if (!(xy > 0 && z > 0 && yawDeg > 0)) return null;
  return { max_xy: xy, max_z: z, max_yaw: (yawDeg * Math.PI) / 180, max_yaw_deg: yawDeg };
}

// Limites d'un profil = limites Agent * ratio. null si limites Agent absentes (fail-closed).
export function profileControlLimits(agentLimits, profile) {
  if (!agentLimits) return null;
  const r = SPEED_PROFILES[profile] ?? SPEED_PROFILES.slow;
  const yawDeg = agentLimits.max_yaw_deg ?? (agentLimits.max_yaw * 180) / Math.PI;
  return {
    max_xy: agentLimits.max_xy * r,
    max_z: agentLimits.max_z * r,
    max_yaw: agentLimits.max_yaw * r,
    max_yaw_deg: yawDeg * r,
    ratio: r,
  };
}

// Valide une politique TAKEOFF (Backend ou Agent). null si min/max non finis ou incohérents.
export function validateTakeoffPolicy(p) {
  if (!p) return null;
  const min = Number(p.min_alt_m), max = Number(p.max_alt_m);
  if (![min, max].every((v) => Number.isFinite(v))) return null;
  if (!(min > 0) || !(max >= min)) return null;
  return { min, max };
}

// Validation EXPLICITE de l'altitude TAKEOFF (jamais de clamp silencieux). {valid, reason}.
export function validateTakeoffAltitude(value, policy) {
  const pol = validateTakeoffPolicy(policy) || (policy && Number.isFinite(policy.min) && Number.isFinite(policy.max) ? policy : null);
  if (!pol) return { valid: false, reason: "policy_unavailable" };
  const n = Number(value);
  if (!Number.isFinite(n)) return { valid: false, reason: "invalid_number" };
  if (n < pol.min) return { valid: false, reason: "below_min" };
  if (n > pol.max) return { valid: false, reason: "above_max" };
  return { valid: true };
}

// Cible combinée depuis un ensemble de directions maintenues (multi-axes ; relâcher un axe le remet à zéro).
export function targetFromDirections(dirs, limits = DEFAULT_LIMITS) {
  const t = zeroTarget();
  const L = _validLimits(limits);
  if (!L) return t;  // fail-closed : pas de limites Agent => cible nulle
  const list = dirs instanceof Set ? Array.from(dirs) : Array.isArray(dirs) ? dirs : [];
  const AX = {
    forward: ["vx", L.max_xy], back: ["vx", -L.max_xy],
    right: ["vy", L.max_xy], left: ["vy", -L.max_xy],
    up: ["vz", -L.max_z], down: ["vz", L.max_z],
    yaw_right: ["yaw_rate", L.max_yaw], yaw_left: ["yaw_rate", -L.max_yaw],
  };
  for (const d of list) {
    const c = AX[d];
    if (c) t[c[0]] += c[1];
  }
  t.vx = clamp(t.vx, -L.max_xy, L.max_xy);
  t.vy = clamp(t.vy, -L.max_xy, L.max_xy);
  t.vz = clamp(t.vz, -L.max_z, L.max_z);
  t.yaw_rate = clamp(t.yaw_rate, -L.max_yaw, L.max_yaw);
  return t;
}

export function directionToTarget(dir, limits = DEFAULT_LIMITS) {
  return isRealDirection(dir) ? targetFromDirections([dir], limits) : zeroTarget();
}

// Construit une trame manual_velocity 4 axes. Défense en profondeur : clamp à l'enveloppe des
// limites AGENT (pas seulement le profil). L'Agent reste le clamp final autoritaire.
export function buildVelocityFrame({ session_id, seq, vx = 0, vy = 0, vz = 0, yaw_rate = 0 }, limits = DEFAULT_LIMITS) {
  const L = _validLimits(limits);
  // Trame zéro toujours possible (PRESTREAM/deadman) ; mais SANS limites valides, aucune sortie non nulle.
  if (!L) return { type: "manual_velocity", session_id, seq, deadman: true, vx: 0, vy: 0, vz: 0, yaw_rate: 0 };
  return {
    type: "manual_velocity",
    session_id,
    seq,
    deadman: true,
    vx: clamp(vx || 0, -L.max_xy, L.max_xy),
    vy: clamp(vy || 0, -L.max_xy, L.max_xy),
    vz: clamp(vz || 0, -L.max_z, L.max_z),
    yaw_rate: clamp(yaw_rate || 0, -L.max_yaw, L.max_yaw),
  };
}

// Orchestration ARM -> TAKEOFF -> AUTO OFFBOARD. AUCUN clamp silencieux : l'altitude doit avoir
// été validée en amont (validateTakeoffAltitude). On envoie exactement la valeur fournie.
export function buildTakeoffStart(altitude) {
  return { type: "auto_takeoff_offboard_start", altitude_m: Number(altitude) };
}

export function buildManualStop(session_id) {
  return { type: "manual_stop", session_id };
}

// Contrat autoritaire : les valeurs de sécurité vivent dans status.safety_snapshot (jamais au top-level).
// Ne fabrique AUCUNE valeur par défaut « sûre » : renvoie {} si absent -> les champs restent inconnus (—).
export function authoritativeSafety(status) {
  return (status && status.safety_snapshot) || {};
}

// HUD RÉEL autoritaire : construit à partir du statut OFFBOARD + safety_snapshot + télémétrie LIVE flotte.
// AUCUN fallback simulation. Donnée absente -> null (affiché "—"), jamais 0 fabriqué.
export function buildRealHud(status, safety, drone) {
  const s = safety || {};
  const d = drone || {};
  const gps = s.gps_fix_type != null ? s.gps_fix_type >= 3 : (d.gps_fix != null ? d.gps_fix : null);
  return {
    mode: status?.px4_mode ?? s.px4_mode ?? d.mode ?? null,
    battery: s.battery_remaining ?? d.battery ?? null,
    gpsFixType: s.gps_fix_type ?? null,
    gps,
    alt: d.relative_alt ?? d.alt ?? null,   // relative_alt UNIQUEMENT (jamais AMSL)
    speed: d.speed ?? null,
    heading: d.heading ?? null,
    lat: d.lat ?? null,
    lng: d.lng ?? null,
    pitch: d.pitch ?? null,
    roll: d.roll ?? null,
    yaw: d.yaw ?? d.heading ?? null,
  };
}

// Drone carte en mode RÉEL : position LIVE uniquement. Position inconnue -> lat/lng null (jamais le départ sim).
export function buildRealMapDrone(droneId, drone) {
  const d = drone || {};
  // MAP.NAV.1 : coordonnées finies (0 valide), jamais un test "truthy".
  const hasPos = isValidLatLng(d.lat, d.lng);
  return {
    drone_id: droneId,
    lat: hasPos ? d.lat : null,
    lng: hasPos ? d.lng : null,
    // UNIFIED.7 : fidélité ONLINE/OFFLINE. Ne JAMAIS fabriquer "online" ; refléter le statut réel.
    status: d.status ?? "unknown",
    battery: d.battery ?? null,
    alt: d.relative_alt ?? d.alt ?? null,
    speed: d.speed ?? null,
    heading: d.heading ?? null,
    gps_fix: d.gps_fix ?? null,
    mode: d.mode ?? null,
    positionKnown: hasPos,
    // MAP.NAV.1 — champs lecture seule nécessaires au modèle de navigation (inconnu = null, jamais inventé).
    last_seen: d.last_seen ?? null,
    fix_type: d.fix_type ?? null,
    gps_state: d.gps_state ?? null,
    global_position_valid: d.global_position_valid ?? null,
    course_over_ground_deg: d.course_over_ground_deg ?? null,
    // MAP.NAV.2 — provenance/âge/qualité du COG mesuré (null si ancien Agent).
    course_source: d.course_source ?? null,
    course_observed_at: d.course_observed_at ?? null,
    course_age_s: d.course_age_s ?? null,
    course_speed_mps: d.course_speed_mps ?? null,
    h_acc_m: d.h_acc_m ?? null,
    hdop: d.hdop ?? null,
  };
}

// Autorité Raspberry : le contrôle réel n'existe que si le Pi le confirme.
export function realEnabledFromStatus(status) {
  return !!status && status.source === "raspberry"
    && status.real_enabled === true && status.dry_run === false;
}

// États Agent conservant l'autorité OFFBOARD (HOLDING = maintien de position, non une perte).
export const AUTHORITY_STATES = ["ACTIVE", "HOLDING"];

// Gating de mouvement (miroir de l'exigence 8). TOUTES les conditions doivent tenir.
// fail-closed : inclut le gate autoritatif backend orchMovementAllowed.
export function movementAllowed({ wsOpen, leaseGranted, status, phase, activeHoldDone, orchMovementAllowed }) {
  if (!wsOpen) return false;
  if (!leaseGranted) return false;
  if (!realEnabledFromStatus(status)) return false;
  if (!status || status.source !== "raspberry") return false;
  if (status.px4_mode !== "OFFBOARD") return false;
  // HOLD.CONTINUITY.1 — HOLDING conserve l'autorité (l'Agent exige une trame neutre avant reprise).
  if (!AUTHORITY_STATES.includes(status.state)) return false;
  if (status.native_exit_pending === true) return false; // R1 : LAND/RTL natif en cours -> verrouillé
  if (phase !== "active") return false;
  if (!activeHoldDone) return false;
  if (orchMovementAllowed !== true) return false; // gate autoritatif orchestration backend
  return true;
}

// UNIFIED.2 — gate PUR fail-closed du départ au sol (ARM+TAKEOFF). N'utilise JAMAIS entryReady
// (qui concerne l'ENTRÉE OFFBOARD après décollage, armed=true/in_air=true). UNKNOWN => false.
// Toutes les conditions doivent tenir ; preflight est le précheck sol autoritaire backend.
export const START_BLOCKING_PHASES = ["offboard_starting", "active_hold", "active", "stabilizing"];
export function canStartFlight({
  conn, sessionId, gatewayForwarded, realEnabled, source, dryRun,
  preflightAllowed, fcBlocked, criticalAction, phase, armed, landedState,
} = {}) {
  if (conn !== "open") return false;
  if (!sessionId) return false;
  if (gatewayForwarded !== true) return false;
  if (realEnabled !== true) return false;
  if (source !== "raspberry") return false;
  if (dryRun !== false) return false;
  if (preflightAllowed !== true) return false;
  if (fcBlocked) return false;
  if (criticalAction) return false;
  if (START_BLOCKING_PHASES.includes(phase)) return false;
  if (armed !== false) return false;
  if (landedState !== 1) return false;
  return true;
}

// Perte d'autorité pendant active/active_hold : le Raspberry n'est plus en OFFBOARD ACTIVE.
// Exclut PILOT_OVERRIDE (traité séparément). fail-closed -> le hook verrouille immédiatement.
export function authorityLostDuringActive({ phase, status }) {
  if (!["active", "active_hold"].includes(phase)) return false;
  if (!status) return false;
  if (status.state === "PILOT_OVERRIDE" || status.pilot_override === true) return false;
  return !AUTHORITY_STATES.includes(status.state) || status.px4_mode !== "OFFBOARD";
}

// PILOT.BLOCKERS.1 — réautorisation du mouvement après safetyZero / action critique : cockpit revenu,
// entrées neutres, NOUVEAU statut Raspberry ACTIVE|HOLDING en OFFBOARD, lease + gate orchestrateur
// valides, aucune sortie native ni action critique. Ne rejoue jamais de vitesse (cible déjà nulle).
export function canRearmMovement({ cockpitFocused, inputsNeutral, freshStatus, status, phase,
                                   leaseGranted, wsOpen, orchMovementAllowed, criticalAction }) {
  if (!cockpitFocused || !inputsNeutral || !freshStatus || !leaseGranted || !wsOpen || criticalAction) return false;
  if (phase !== "active" || !status || status.source !== "raspberry") return false;
  if (!AUTHORITY_STATES.includes(status.state) || status.px4_mode !== "OFFBOARD") return false;
  if (status.native_exit_pending === true || status.pilot_override === true) return false;
  return orchMovementAllowed === true;
}

// HOLD.CONTINUITY.1 — entrée en HOLDING : verrou « neutre requis » sur les entrées encore tenues.
// Renvoie {dirs:Set, analog:bool}. Une entrée verrouillée n'agit plus jusqu'à son relâchement.
export function holdLatchFrom(heldDirs, analogActive) {
  return { dirs: new Set(heldDirs || []), analog: !!analogActive };
}

// ---- Commandes critiques LAND / RTL (pipeline LOT1, idempotence + suivi read-only) ----

// command_id client généré UNE SEULE FOIS par action critique.
export function newClientCommandId() {
  const uuid = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}${Math.random().toString(16).slice(2)}`;
  return uuid.replace(/-/g, "");
}

// États terminaux d'échec (débloquent l'opérateur ; JAMAIS de retry automatique).
export const CRITICAL_FAIL_STATUSES = ["failed", "timeout", "expired", "precheck_failed", "px4_rejected"];
export function isCriticalFailStatus(s) { return CRITICAL_FAIL_STATUSES.includes(s); }
export function isUncertainStatus(s) { return s === "result_unknown"; }

// Un second clic critique est interdit tant qu'une action critique est active.
export function canStartCritical(criticalAction) { return !criticalAction; }

// Cycle PHYSIQUE terminé (déverrouillage) :
//   LAND  : status=executed (le backend/Agent garantit landed=true, armed=false).
//   RTL   : status=executed PUIS télémétrie landed_state=1 & armed=false (RTL executed = mode confirmé seulement).
export function criticalUnlockReady({ type, status, armed, landed_state }) {
  if (type === "land") return status === "executed";
  if (type === "rtl") return status === "executed" && armed === false && landed_state === 1;
  return false;
}

// POST idempotent (X-Command-Id) — AUCUN retry ici (le hook ne rappelle jamais ce POST pour la même action).
export async function postCriticalCommand(apiClient, droneId, type, commandId) {
  const path = type === "land" ? `/drones/${droneId}/land` : `/drones/${droneId}/rtl`;
  const { data } = await apiClient.post(path, null, { headers: { "X-Command-Id": commandId } });
  return data;
}

// Suivi READ-ONLY d'une commande par son command_id (jamais de POST).
export async function fetchCommand(apiClient, droneId, commandId) {
  const { data } = await apiClient.get(`/drones/${droneId}/commands/${commandId}`);
  return data;
}
